import { createSignal, createEffect, onMount, onCleanup, Show, For, useContext } from 'solid-js';
import { gql } from 'graphql-request';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Button } from '../../ui-components/button/button';
import decodeHtmlEntities from '../../tools/decode-html-entities';

const GET_HOSPITALS_QUERY = gql`
    query GetHospitals($key: String!) {
        hospitalsData: resource(key: $key) {
            content
        }
    }
`;

const GET_HOSPITAL_SETTINGS_QUERY = gql`
    query GetHospitalSettings($key: String!) {
        hospitalSettings: resource(key: $key) {
            content
        }
    }
`;

type CategoryConfig = {
    key: string;
    label: string;
    color: string;
};

type Hospital = {
    id: number;
    name: string;
    slug?: string;
    centerType?: string;
    locationName?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    latitude: number;
    longitude: number;
    contactPerson?: string;
    email?: string;
    phone?: string;
    indications?: string[];
    cuffStatus?: string;
    exclusionCriteria?: string;
    religiousOrientation?: string;
    capacity?: number;
};

const CUFF_STATUS_SLUGS: Record<string, { slug: string; fallback: string }> = {
    'with_and_without': { slug: 'hospital-map-cuff-with-and-without', fallback: 'With and without cuff' },
    'without_only': { slug: 'hospital-map-cuff-without-only', fallback: 'Without cuff only' },
};

const DEFAULT_MARKER_COLOR = '#918787';

const formatAddress = (hospital: Hospital): string => {
    const parts = [hospital.street, hospital.postalCode, hospital.city].filter(Boolean);
    return parts.join(', ');
};

export const HospitalMap = () => {
    const { localize, createCachedResource, azureMapsSubscriptionKey } = useContext(AppContext);
    
    let mapContainer: HTMLDivElement | undefined;
    let hospitalDetailRef: HTMLDivElement | undefined;
    let map: any;   
    let popup: any;
    let datasource: any;
    let atlas: any;

    const [selectedHospital, setSelectedHospital] = createSignal<Hospital | null>(null);
    const [mapLoaded, setMapLoaded] = createSignal(false);
    const [mapError, setMapError] = createSignal<string | null>(null);
    const [mounted, setMounted] = createSignal(false);
    const [hospitals, setHospitals] = createSignal<Hospital[]>([]);
    const [categories, setCategories] = createSignal<CategoryConfig[]>([]);
    const [selectedCategories, setSelectedCategories] = createSignal<Set<string>>(new Set());

    const categoryColorMap = () => {
        const colorMap: Record<string, string> = {};
        for (const cat of categories()) {
            colorMap[cat.key] = cat.color;
        }
        return colorMap;
    };

    const categoryColor = (key: string) => categoryColorMap()[key] || DEFAULT_MARKER_COLOR;

    const centerTypeLabel = (type: string) => {
        const cat = categories().find(c => c.key === type);
        return cat ? cat.label : type;
    };

    const isAllSelected = () => selectedCategories().size === 0;

    const filteredHospitals = () => {
        const selected = selectedCategories();
        if (selected.size === 0) return hospitals();
        return hospitals().filter(h => h.centerType && selected.has(h.centerType));
    };

    const toggleCategory = (category: string) => {
        const next = new Set(selectedCategories());
        if (next.has(category)) {
            next.delete(category);
        } else {
            next.add(category);
        }
        setSelectedCategories(next);
    };

    const selectAll = () => {
        setSelectedCategories(new Set<string>());
    };

    const [hospitalsData] = createCachedResource(GET_HOSPITALS_QUERY, { key: '/wp-json/rawb/v1/hospitals' }, true);
    const [settingsData] = createCachedResource(GET_HOSPITAL_SETTINGS_QUERY, { key: '/wp-json/rawb/v1/hospital-settings' }, true);

    createEffect(() => {
        const data = settingsData();
        if (data?.hospitalSettings?.content?.categories) {
            const cats: CategoryConfig[] = data.hospitalSettings.content.categories;
            setCategories(cats);
        }
    });

    createEffect(() => {
        const data = hospitalsData();
        if (data?.hospitalsData?.content?.hospitals && data.hospitalsData.content.hospitals.length > 0) {
            setHospitals(data.hospitalsData.content.hospitals);
        }
    });

    createEffect(() => {
        const list = filteredHospitals();
        if (mapLoaded() && map && datasource && atlas) {
            updateMapMarkers(list);
        }
    });

    const fitMapToMarkers = (hospitalList: Hospital[]) => {
        if (!map || !atlas || hospitalList.length === 0) return;

        if (hospitalList.length === 1) {
            map.setCamera({
                center: [hospitalList[0].longitude, hospitalList[0].latitude],
                zoom: 9,
                type: 'ease',
                duration: 300,
            });
        } else {
            const positions = hospitalList.map((h: Hospital) => [h.longitude, h.latitude]);
            const bounds = atlas.data.BoundingBox.fromPositions(positions);
            map.setCamera({
                bounds,
                padding: 50,
                type: 'ease',
                duration: 300,
            });
        }
    };

    const updateMapMarkers = (hospitalList: Hospital[]) => {
        if (!datasource || !atlas) return;

        datasource.clear();

        const features = hospitalList.map((hospital: Hospital) => {
            const color = hospital.centerType ? categoryColor(hospital.centerType) : DEFAULT_MARKER_COLOR;
            return new atlas.data.Feature(
                new atlas.data.Point([hospital.longitude, hospital.latitude]),
                {
                    id: hospital.id,
                    name: hospital.name,
                    color,
                    centerType: hospital.centerType || '',
                    locationName: hospital.locationName || '',
                    street: hospital.street || '',
                    postalCode: hospital.postalCode || '',
                    city: hospital.city || '',
                }
            );
        });

        datasource.add(features);
        fitMapToMarkers(hospitalList);
    };

    const cuffStatusLabel = (status: string) => {
        const entry = CUFF_STATUS_SLUGS[status];
        return entry ? localize(entry.slug, entry.fallback) : status;
    };

    const createPopupContent = (hospital: Hospital): string => {
        const catLabel = hospital.centerType ? centerTypeLabel(hospital.centerType) : '';
        const catColor = hospital.centerType ? categoryColor(hospital.centerType) : DEFAULT_MARKER_COLOR;
        const address = formatAddress(hospital);

        let content = `<div style="padding: 16px 20px 20px 20px; max-width: 320px; font-family: inherit;"><h3 style="margin: 0 0 4px 0; font-size: 16px; font-weight: bold; color: #555151;">${hospital.name}</h3>`;

        if (hospital.locationName) {
            content += `<p style="margin: 0 0 8px 0; font-size: 13px; color: #736C6C;">${hospital.locationName}</p>`;
        }

        if (catLabel) {
            content += `<p style="margin: 0 0 10px 0; font-size: 12px; color: ${catColor}; font-weight: 600;">${catLabel}</p>`;
        }

        if (address) {
            content += `<p style="margin: 0; font-size: 13px; color: #736C6C;">${address}</p>`;
        }

        content += '</div>';
        return content;
    };

    const showPopupForHospital = (hospital: Hospital) => {
        if (!popup || !map) return;

        popup.setOptions({
            content: createPopupContent(hospital),
            position: [hospital.longitude, hospital.latitude],
            pixelOffset: [0, -12],
        });
        popup.open(map);
    };

    const initMap = async () => {
        if (!mapContainer) return;

        try {
            atlas = await import('azure-maps-control');

            if (!document.querySelector('link[href*="atlas.microsoft.com"]')) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.css';
                document.head.appendChild(link);
            }

            map = new atlas.Map(mapContainer, {
                center: [10.0, 50.0],
                zoom: 2,
                language: 'en-US',
                style: 'road',
                showLogo: false,
                showFeedbackLink: false,
                authOptions: {
                    authType: atlas.AuthenticationType?.subscriptionKey ?? 'subscriptionKey',
                    subscriptionKey: azureMapsSubscriptionKey,
                },
            });

            map.events.add('ready', () => {
                map.controls.add(new atlas.control.ZoomControl(), { position: 'top-right' as any });

                // Hide POI labels to match previous Google Maps behaviour
                try {
                    const style = map.map.getStyle();
                    if (style?.layers) {
                        for (const layer of style.layers) {
                            if (layer.id?.includes('poi') || layer.id?.includes('point_of_interest')) {
                                map.map.setLayoutProperty(layer.id, 'visibility', 'none');
                            }
                        }
                    }
                } catch {
                    // Silently ignore if layers aren't available yet
                }

                datasource = new atlas.source.DataSource();
                map.sources.add(datasource);

                const bubbleLayer = new atlas.layer.BubbleLayer(datasource, null, {
                    radius: 10,
                    color: ['get', 'color'],
                    strokeColor: '#ffffff',
                    strokeWidth: 2,
                });
                map.layers.add(bubbleLayer);

                popup = new atlas.Popup({ closeButton: true });

                map.events.add('click', bubbleLayer, (e: any) => {
                    if (e.shapes && e.shapes.length > 0) {
                        const properties = e.shapes[0].getProperties();
                        const hospital = hospitals().find(h => h.id === properties.id);
                        if (hospital) {
                            setSelectedHospital(hospital);
                            scrollToDetail();
                            showPopupForHospital(hospital);
                            map.setCamera({
                                center: [hospital.longitude, hospital.latitude],
                                type: 'ease',
                                duration: 300,
                            });
                        }
                    }
                });

                map.events.add('mouseover', bubbleLayer, () => {
                    map.getCanvasContainer().style.cursor = 'pointer';
                });
                map.events.add('mouseout', bubbleLayer, () => {
                    map.getCanvasContainer().style.cursor = '';
                });

                updateMapMarkers(filteredHospitals());
                setMapLoaded(true);
            });

            map.events.add('error', (e: any) => {
                console.error('Azure Maps error:', e.error);
                // Only show fatal error if the map never loaded (e.g. bad auth key)
                if (!mapLoaded()) {
                    setMapError(localize('hospital-map-load-error', 'Failed to load map.'));
                }
            });
        } catch (err) {
            console.error('Failed to load Azure Maps:', err);
            setMapError(localize('hospital-map-load-error', 'Failed to load map.'));
        }
    };

    onMount(() => {
        setMounted(true);
        initMap();
    });

    onCleanup(() => {
        map?.dispose();
    });

    const scrollToDetail = () => {
        setTimeout(() => {
            hospitalDetailRef?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
    };

    const handleHospitalClick = (hospital: Hospital) => {
        setSelectedHospital(hospital);
        scrollToDetail();

        if (map && popup) {
            showPopupForHospital(hospital);
            map.setCamera({
                center: [hospital.longitude, hospital.latitude],
                zoom: 9,
                type: 'ease',
                duration: 300,
            });
        }
    };

    const resetView = () => {
        if (map) {
            popup?.close();
            setSelectedHospital(null);
            setSelectedCategories(new Set<string>());
            fitMapToMarkers(hospitals());
        }
    };

    return (
        <div>
            <Show when={mapError()}>
                <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {mapError()}
                </div>
            </Show>

            {/* Category Filter */}
            <Show when={mounted() && categories().length > 0}>
                <div class="mb-8 flex flex-wrap gap-3">
                    <button
                        class={`flex items-center gap-2 px-4 py-2.5 rounded-medium border text-small cursor-pointer transition-all duration-200 ${
                            isAllSelected()
                                ? 'border-grey-300 bg-white shadow-sm'
                                : 'border-grey-200 bg-grey-100'
                        }`}
                        onClick={selectAll}
                    >
                        {localize('hospital-map-all', 'All')}
                    </button>
                    <For each={categories()}>
                        {(category) => {
                            const isActive = () => selectedCategories().has(category.key);
                            return (
                                <button
                                    class={`flex items-center gap-2 px-4 py-2.5 rounded-medium border text-small cursor-pointer transition-all duration-200 ${
                                        isActive()
                                            ? 'border-grey-300 bg-white shadow-sm'
                                            : 'border-grey-200 bg-grey-100'
                                    }`}
                                    onClick={() => toggleCategory(category.key)}
                                >
                                    <span
                                        class="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                        style={{ background: category.color }}
                                    />
                                    {decodeHtmlEntities(category.label)}
                                </button>
                            );
                        }}
                    </For>
                </div>
            </Show>

            <div class="flex flex-col lg:flex-row gap-8">
                {/* Map Container */}
                <div class="flex-1">
                    <div class="relative">
                        <div
                            ref={mapContainer}
                            class="w-full h-[500px] lg:h-[600px] rounded-medium border border-grey-200 bg-grey-100"
                        />
                        <Show when={!mapLoaded() && !mapError()}>
                            <div class="absolute inset-0 flex items-center justify-center bg-grey-100 rounded-medium">
                                <p class="text-normal text-grey-400">{localize('hospital-map-loading', 'Loading map...')}</p>
                            </div>
                        </Show>
                    </div>

                    <Show when={mounted()}>
                        <div class="mt-6 flex gap-4">
                            <Button
                                variant="secondary"
                                label={localize('hospital-map-reset-view', 'Reset view')}
                                onClick={resetView}
                            />
                        </div>
                    </Show>

                    {/* Selected Hospital Details */}
                    <Show when={selectedHospital()}>
                        {(hospital) => (
                            <div
                                ref={hospitalDetailRef}
                                class="mt-6 bg-white rounded-medium border border-grey-200 p-6 overflow-hidden border-l-4"
                                style={{ 'border-left-color': hospital().centerType ? categoryColor(hospital().centerType!) : DEFAULT_MARKER_COLOR }}
                            >
                                <h3 class="text-normal font-bold mb-1 text-primary">
                                    {decodeHtmlEntities(hospital().name)}
                                </h3>
                                <Show when={hospital().locationName}>
                                    <p class="text-small text-grey-500 mb-1">{hospital().locationName}</p>
                                </Show>
                                <Show when={hospital().centerType}>
                                    <p class="text-smaller font-semibold mb-4" style={{ color: categoryColor(hospital().centerType!) }}>
                                        {centerTypeLabel(hospital().centerType!)}
                                    </p>
                                </Show>

                                <div class="space-y-3 text-small">
                                    <Show when={formatAddress(hospital())}>
                                        <p><strong>{localize('hospital-map-address', 'Address')}:</strong> {formatAddress(hospital())}</p>
                                    </Show>
                                    <Show when={hospital().contactPerson}>
                                        <p><strong>{localize('hospital-map-contact-person', 'Contact person')}:</strong> {hospital().contactPerson}</p>
                                    </Show>
                                    <Show when={hospital().email}>
                                        <p><strong>{localize('hospital-map-email', 'Email')}:</strong> {hospital().email}</p>
                                    </Show>
                                    <Show when={hospital().phone}>
                                        <p><strong>{localize('hospital-map-phone', 'Phone')}:</strong> <a href={`tel:${hospital().phone}`} class="underline">{hospital().phone}</a></p>
                                    </Show>
                                    <Show when={hospital().indications && hospital().indications!.length > 0}>
                                        <p><strong>{localize('hospital-map-indications', 'Indications')}:</strong> {hospital().indications!.join(', ')}</p>
                                    </Show>
                                    <Show when={hospital().cuffStatus}>
                                        <p><strong>{localize('hospital-map-cuff-status', 'Cuff status')}:</strong> {cuffStatusLabel(hospital().cuffStatus!)}</p>
                                    </Show>
                                    <Show when={hospital().exclusionCriteria}>
                                        <p><strong>{localize('hospital-map-exclusion-criteria', 'Exclusion criteria')}:</strong> {hospital().exclusionCriteria}</p>
                                    </Show>
                                    <Show when={hospital().religiousOrientation}>
                                        <p><strong>{localize('hospital-map-religious-orientation', 'Religious orientation')}:</strong> {hospital().religiousOrientation}</p>
                                    </Show>
                                    <Show when={hospital().capacity}>
                                        <p><strong>{localize('hospital-map-capacity', 'Capacity')}:</strong> {hospital().capacity}</p>
                                    </Show>
                                </div>
                            </div>
                        )}
                    </Show>
                </div>

                {/* Hospital List Sidebar */}
                <div class="w-full lg:w-96">
                    <h2 class="text-normal font-bold mb-4 text-primary">
                        {localize('hospital-map-hospitals', 'Hospitals')} ({filteredHospitals().length})
                    </h2>

                    <div class="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                        <Show when={filteredHospitals().length === 0}>
                            <p class="text-small text-grey-400 py-8 text-center">
                                {localize('hospital-map-no-hospitals', 'No hospitals found')}
                            </p>
                        </Show>
                        <For each={filteredHospitals()}>
                            {(hospital) => (
                                <button
                                    class={`block w-full text-left p-5 rounded-medium border cursor-pointer transition-all duration-200 ${
                                        selectedHospital()?.id === hospital.id
                                            ? 'border-blue-400 bg-blue-100 shadow-sm'
                                            : 'border-grey-200 bg-white hover:shadow-sm hover:border-grey-300'
                                    }`}
                                    onClick={() => handleHospitalClick(hospital)}
                                >
                                    <div class="flex items-center gap-2.5 mb-1">
                                        <Show when={hospital.centerType}>
                                            <span
                                                class="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                                style={{ background: categoryColor(hospital.centerType!) }}
                                            />
                                        </Show>
                                        <span class="font-bold text-small text-primary">{decodeHtmlEntities(hospital.name)}</span>
                                    </div>
                                    <Show when={hospital.locationName}>
                                        <div class="text-smaller text-grey-500 mb-1">{hospital.locationName}</div>
                                    </Show>
                                    <Show when={hospital.centerType}>
                                        <div class="text-smaller text-grey-400 mb-2">
                                            {centerTypeLabel(hospital.centerType!)}
                                        </div>
                                    </Show>
                                    <Show when={hospital.city}>
                                        <div class="text-smaller text-grey-500">{hospital.city}</div>
                                    </Show>
                                </button>
                            )}
                        </For>
                    </div>
                </div>
            </div>
        </div>
    );
};
